// components/Settings/GeneralSettings/RecordingPreferences.js
import { Button, Card, CardBody, Input, Select, SelectItem, Switch } from '@heroui/react'

/**
 * Controls the "use bland url" checkbox,
 * the "always encrypted vs. infinite vs. custom" lifespan logic,
 * and triggers a confirmation when saving.
 *
 * Props:
 *   - useBlandUrl, setUseBlandUrl
 *   - lifespanMode, setLifespanMode ("always_encrypted", "infinite", or "custom")
 *   - customDays, setCustomDays
 *   - setShowRecordingModal (to open the modal)
 */
export default function RecordingPreferences({
  useBlandUrl,
  setUseBlandUrl,
  lifespanMode,
  setLifespanMode,
  customDays,
  setCustomDays,
  liveListen,
  setLiveListen,
  recordBlandCallOnly,
  setRecordBlandCallOnly,
  setShowRecordingModal,
}) {
  return (
    <Card
      radius="none"
      shadow="none"
      className="mt-8 bg-gray-50"
    >
      <CardBody className="p-6">
        <div className="mb-5">
          <h3 className="mb-2 text-lg font-semibold text-gray-900">
            Recording Preferences
          </h3>
          <p className="text-sm leading-relaxed text-gray-600">
            Configure how recordings are stored and accessed across your organization.
            These settings apply to all team members and affect recording security and availability.
          </p>
        </div>

        {/* URL Branding Section */}
        <Card
          radius="none"
          shadow="none"
          className="mb-6 border border-gray-200 bg-white"
        >
          <CardBody className="p-4">
            <div className="flex items-start gap-3">
              <Switch
                radius="none"
                size="sm"
                isSelected={useBlandUrl}
                onValueChange={setUseBlandUrl}
                classNames={{
                  wrapper: 'mt-0.5',
                }}
              />
              <div className="flex-1">
                <label className={`
                  block cursor-pointer text-sm font-medium text-gray-700
                `}
                >
                  Use Bland URLs
                </label>
                <p className="mt-1 text-xs leading-tight text-gray-500">
                  Wrap recording URLs with bland's domain instead of showing raw Twilio links.
                  Provides a more professional appearance and additional URL obfuscation.
                </p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Recording Lifespan Section - Only show if using Bland URLs */}
        {useBlandUrl && (
          <Card
            radius="none"
            shadow="none"
            className="mb-6 border border-gray-200 bg-white"
          >
            <CardBody className="p-4">
              <div className="mb-3">
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Recording Security & Retention
                </label>
                <p className="mb-3 text-xs leading-tight text-gray-500">
                  Choose how long recording links remain accessible before automatic encryption
                </p>
              </div>

              <Select
                radius="none"
                size="sm"
                selectedKeys={[lifespanMode]}
                onSelectionChange={keys => setLifespanMode(Array.from(keys)[0])}
                className="max-w-80"
                classNames={{
                  trigger: 'bg-white border border-gray-300',
                }}
              >
                <SelectItem key="always_encrypted" value="always_encrypted">
                  🔒 Always Encrypted (Maximum Security)
                </SelectItem>
                <SelectItem key="infinite" value="infinite">
                  ♾️ Never Expire (Always Accessible)
                </SelectItem>
                <SelectItem key="custom" value="custom">
                  ⏰ Custom Duration
                </SelectItem>
              </Select>

              {/* Description for each option */}
              <div className={`
                mt-3 rounded border border-gray-200 bg-gray-50 p-3
              `}
              >
                {lifespanMode === 'always_encrypted' && (
                  <p className="m-0 text-xs leading-tight text-green-700">
                    <strong>Maximum Security:</strong>
                    {' '}
                    Recordings are immediately encrypted after call completion.
                    Links become inaccessible right away, ensuring maximum data protection and compliance.
                  </p>
                )}
                {lifespanMode === 'infinite' && (
                  <p className="m-0 text-xs leading-tight text-blue-700">
                    <strong>Always Accessible:</strong>
                    {' '}
                    Recording links never expire and remain accessible indefinitely.
                    Best for long-term storage and easy access to historical calls.
                  </p>
                )}
                {lifespanMode === 'custom' && (
                  <p className="m-0 text-xs leading-tight text-amber-700">
                    <strong>Custom Duration:</strong>
                    {' '}
                    Set a specific number of days (1-1825) before recording links expire.
                    Balances accessibility with security based on your needs.
                  </p>
                )}
              </div>

              {/* Custom days input */}
              {lifespanMode === 'custom' && (
                <div className="mt-4">
                  <label className={`
                    mb-2 block text-xs font-medium text-gray-700
                  `}
                  >
                    Days until expiration (1-1825):
                  </label>
                  <Input
                    radius="none"
                    size="sm"
                    placeholder="e.g. 30"
                    value={customDays}
                    onChange={e => setCustomDays(e.target.value)}
                    className="max-w-32"
                    classNames={{
                      input: 'bg-white',
                      inputWrapper: 'border-gray-300 bg-white',
                    }}
                  />
                </div>
              )}
            </CardBody>
          </Card>
        )}

        {/* Live Listen Section */}
        {/* <Card
          radius="none"
          shadow="none"
          className="mb-6 border border-gray-200 bg-white"
        >
          <CardBody className="p-4">
            <div className="flex items-start gap-3">
              <Switch
                radius="none"
                size="sm"
                isSelected={liveListen}
                onValueChange={setLiveListen}
                classNames={{
                  wrapper: 'mt-0.5',
                }}
              />
              <div className="flex-1">
                <label className={`
                  block cursor-pointer text-sm font-medium text-gray-700
                `}
                >
                  Enable Live Call Monitoring
                </label>
                <p className="mt-1 text-xs leading-tight text-gray-500">
                  Create real-time audio streams for every call. Access live calls through the
                  "Active Calls" dashboard or via the live listen API endpoint for monitoring and coaching.
                </p>
              </div>
            </div>
          </CardBody>
        </Card> */}

        {/* Agent-Only Recording Section */}
        <Card
          radius="none"
          shadow="none"
          className="mb-6 border border-gray-200 bg-white"
        >
          <CardBody className="p-4">
            <div className="flex items-start gap-3">
              <Switch
                radius="none"
                size="sm"
                isSelected={recordBlandCallOnly}
                onValueChange={setRecordBlandCallOnly}
                classNames={{
                  wrapper: 'mt-0.5',
                }}
              />
              <div className="flex-1">
                <label className={`
                  block cursor-pointer text-sm font-medium text-gray-700
                `}
                >
                  Agent-Only Recording
                </label>
                <p className="mt-1 text-xs leading-tight text-gray-500">
                  Automatically stop recording when calls are transferred or handed off from your AI agent
                  to human representatives. Ensures only AI-managed portions are captured.
                </p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Save button => triggers confirmation modal */}
        <div className="flex justify-end border-t border-gray-200 pt-4">
          <Button
            radius="none"
            color="primary"
            size="sm"
            className="px-5 py-2"
            onPress={() => setShowRecordingModal(true)}
          >
            Save Recording Preferences
          </Button>
        </div>
      </CardBody>
    </Card>
  )
}
